import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, XCircle, Loader2, Copy, Check, Gift } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NewsletterConfirm = () => {
  const { token } = useParams();
  const { t } = useTranslation();
  const [state, setState] = useState({ status: 'loading', email: '', coupon: null });
  const [copied, setCopied] = useState(false);
  const calledRef = useRef(false);

  useEffect(() => {
    if (calledRef.current) return; // guard StrictMode double-invoke (one-shot token)
    calledRef.current = true;
    const run = async () => {
      try {
        const res = await fetch(`${API_URL}/api/newsletter/confirm/${encodeURIComponent(token)}`);
        if (!res.ok) {
          setState({ status: 'invalid', email: '', coupon: null });
          return;
        }
        const data = await res.json();
        setState({
          status: data.status || 'confirmed',
          email: data.email || '',
          coupon: data.coupon || null,
        });
      } catch (_e) {
        setState({ status: 'invalid', email: '', coupon: null });
      }
    };
    if (token) run();
  }, [token]);

  const copyCode = async () => {
    if (!state.coupon?.code) return;
    try {
      await navigator.clipboard.writeText(state.coupon.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_e) {
      // best-effort
    }
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4 py-16">
      <div className="max-w-md w-full bg-white shadow-md rounded-2xl p-8 text-center" data-testid="newsletter-confirm-card">
        {state.status === 'loading' && (
          <>
            <Loader2 className="h-12 w-12 mx-auto text-orange-500 animate-spin" />
            <h1 className="text-2xl font-bold mt-4">{t('newsletter.confirm_loading', 'Overujeme váš odkaz...')}</h1>
          </>
        )}
        {(state.status === 'confirmed' || state.status === 'already_confirmed') && (
          <>
            <CheckCircle2 className="h-14 w-14 mx-auto text-green-500" />
            <h1 className="text-2xl font-bold mt-4" data-testid="newsletter-confirm-success">
              {state.status === 'already_confirmed'
                ? t('newsletter.confirm_already', 'Už ste prihlásený na odber.')
                : t('newsletter.confirm_success', 'Odber potvrdený, vitajte!')}
            </h1>
            {state.email && (
              <p className="text-gray-500 mt-2 break-all">{state.email}</p>
            )}

            {state.coupon && (
              <div
                className="mt-6 border-2 border-dashed border-orange-300 bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-5 text-left"
                data-testid="newsletter-confirm-coupon"
              >
                <div className="flex items-center gap-2 text-orange-600 font-semibold">
                  <Gift className="h-5 w-5" />
                  <span>
                    {t('newsletter.coupon_title', 'Váš kupón -{{percent}}%', { percent: state.coupon.discount_percent })}
                  </span>
                </div>
                <p className="text-xs text-gray-600 mt-1">
                  {t('newsletter.coupon_terms', 'Pri nákupe nad {{min}}€. Platnosť do {{until}}.', {
                    min: Math.round(state.coupon.min_order_total || 0),
                    until: (state.coupon.valid_until || '').slice(0, 10),
                  })}
                </p>
                <div className="mt-3 flex items-center justify-between gap-2 bg-white border rounded-lg px-3 py-2">
                  <code className="font-mono text-lg font-bold text-orange-700 tracking-widest" data-testid="newsletter-confirm-coupon-code">
                    {state.coupon.code}
                  </code>
                  <button
                    type="button"
                    onClick={copyCode}
                    className="px-2.5 py-1 text-xs bg-orange-500 hover:bg-orange-600 text-white rounded inline-flex items-center gap-1"
                    data-testid="newsletter-confirm-coupon-copy"
                  >
                    {copied
                      ? <><Check className="h-3.5 w-3.5" />{t('newsletter.coupon_copied', 'Skopírované')}</>
                      : <><Copy className="h-3.5 w-3.5" />{t('newsletter.coupon_copy', 'Kopírovať')}</>}
                  </button>
                </div>
              </div>
            )}

            <Link
              to="/catalog"
              className="mt-6 inline-block px-6 py-2.5 rounded-md bg-orange-500 hover:bg-orange-600 text-white font-medium transition"
              data-testid="newsletter-confirm-home"
            >
              {state.coupon
                ? t('newsletter.confirm_shop', 'Použiť kupón v obchode')
                : t('newsletter.confirm_home', 'Späť na úvod')}
            </Link>
          </>
        )}
        {state.status === 'invalid' && (
          <>
            <XCircle className="h-14 w-14 mx-auto text-red-500" />
            <h1 className="text-2xl font-bold mt-4" data-testid="newsletter-confirm-invalid">
              {t('newsletter.confirm_invalid', 'Odkaz je neplatný alebo expirovaný.')}
            </h1>
            <p className="text-gray-500 mt-2">
              {t('newsletter.confirm_invalid_help', 'Skúste sa prihlásiť na odber znova z päty stránky.')}
            </p>
            <Link
              to="/"
              className="mt-6 inline-block px-6 py-2.5 rounded-md bg-orange-500 hover:bg-orange-600 text-white font-medium transition"
            >
              {t('newsletter.confirm_home', 'Späť na úvod')}
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default NewsletterConfirm;
